/**
 * Shared stale-deploy chunk-error self-heal.
 *
 * When a deploy ships while a tab/app is open, lazy-loading a route can fail
 * with "Failed to fetch dynamically imported module" / "Importing a module
 * script failed" — the old page asking for a chunk hash that no longer exists.
 * Reloading fetches the fresh index.html → new chunk map → fixed.
 *
 * These helpers are shared by main.tsx (window-level `vite:preloadError`,
 * `unhandledrejection`, and `error` listeners) AND ErrorBoundary
 * (componentDidCatch), because a failed dynamic import that happens INSIDE
 * React rendering (a lazy() component under Suspense) is caught by the React
 * error boundary and never surfaces as any of the three window events — so the
 * window listeners alone miss it (Sentry "Importing a module script failed",
 * source=ErrorBoundary:page, build 1.1.471 · ios). Sharing the SAME
 * one-reload-per-minute guard across all paths prevents double reloads.
 */

// Match the (specific) WebKit and Vite wordings for a failed dynamic import.
export const CHUNK_ERROR_RE =
  /importing a module script failed|failed to fetch dynamically imported module|error loading dynamically imported module/i

// WebKit/iOS also throws a BARE `TypeError: Load failed` when a dynamically
// imported chunk fails to fetch — with none of the module-specific wording
// above (Sentry: `TypeError: Load failed` fetching capacitor://localhost/
// assets/<chunk>.js, build 1.1.89x · ios). This wording is too generic to
// trust at the window level (any failed fetch — an API/image request — says
// "Load failed" too), so it's matched ONLY inside the React error boundary,
// where a render-phase throw is almost always exactly a failed lazy() import.
const WEBKIT_LOAD_FAILED_RE = /\bload failed\b/i

const PRELOAD_RELOAD_KEY = 'trybeup_preload_reload_at'

/**
 * Reload the app to pull a fresh bundle — at most once per minute. Returns true
 * if a reload was triggered, false if rate-limited (so the caller can fall
 * through to showing an error UI instead of looping). The guard also protects
 * against a reload loop when the failure is something other than a stale chunk
 * (e.g. genuinely offline): after one reload the error surfaces normally.
 */
export function selfHealChunkReload(): boolean {
  // Fail CLOSED: the per-minute guard lives in sessionStorage, and
  // window.location.reload() wipes all in-memory module state — so if storage
  // is unavailable (blocked/quota in some iOS WebView configs) we CANNOT
  // prevent a reload→same-error→reload loop. In that case don't auto-reload;
  // let the ErrorBoundary fallback + its manual "Reload" button handle it.
  let last = 0
  try {
    last = Number(sessionStorage.getItem(PRELOAD_RELOAD_KEY) || 0)
  } catch {
    return false  // can't read the guard → don't risk a loop
  }
  if (Date.now() - last < 60_000) return false
  try {
    sessionStorage.setItem(PRELOAD_RELOAD_KEY, String(Date.now()))
  } catch {
    return false  // can't persist the guard → don't risk a loop
  }
  window.location.reload()
  return true
}

/** True if the message looks like a stale-deploy dynamic-import failure. */
export function isChunkLoadError(message: string | undefined | null): boolean {
  return !!message && CHUNK_ERROR_RE.test(message)
}

/**
 * Boundary-only variant: also treats WebKit's bare "Load failed" as a chunk
 * error. Use this ONLY from the React error boundary (a render-phase throw),
 * never from the window-level handlers — there "Load failed" could be any
 * failed fetch. Returns true for both the specific wordings and the bare
 * WebKit one so the iOS app self-heals a failed lazy() import.
 */
export function isRenderChunkLoadError(message: string | undefined | null): boolean {
  return isChunkLoadError(message) || (!!message && WEBKIT_LOAD_FAILED_RE.test(message))
}
